import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

/**
 * Die Sperrliste.
 *
 * Die Regeln entscheiden vor JEDER Nachricht, ob sie hinausgeht — geprueft im
 * Worker, beim Kampagnenaufbau und beim Import. Bis hierher war das eine
 * Blackbox: die Endpunkte gab es seit Anfang an, eine Oberflaeche nicht. Wer
 * wissen wollte, warum eine Adresse keine Post bekommt, konnte nur raten.
 */

export type SuppressionScope = 'email' | 'domain' | 'pattern' | 'hash';

export interface Suppression {
  id: string;
  scope: SuppressionScope;
  value: string;
  reason: string;
  source: string | null;
  note: string | null;
  createdAt: string;
  expiresAt: string | null;
}

export const suppressionsQuery = (search: string) =>
  queryOptions({
    queryKey: ['suppressions', search],
    queryFn: ({ signal }) =>
      api.get<{ items: Suppression[] }>(
        `/api/suppressions${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        signal,
      ),
    staleTime: 10_000,
  });

export function useCreateSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      scope: SuppressionScope;
      value: string;
      reason: string;
      note?: string;
    }) => api.post<{ suppression: Suppression }>('/api/suppressions', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

export interface SuppressionImportResult {
  created: number;
  duplicates: number;
  invalid: string[];
}

export function useImportSuppressions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { text: string; reason: string }) =>
      api.post<SuppressionImportResult>('/api/suppressions/import', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

export function useDeleteSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/suppressions/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}
